import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  BarChart3, 
  TrendingUp, 
  Download, 
  Calendar, 
  Filter,
  FileText,
  PieChart,
  Users,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Loader2,
  RefreshCw,
  Shield,
  AlertTriangle,
  Activity,
  Eye,
  ClipboardCheck,
  Scale
} from 'lucide-react';
import {
  formatQuantidade,
  formatQuantidadeComUnidade,
  formatTotaisPorUnidade,
  agregarQuantidadesPorUnidade,
  toListaPorUnidade
} from '../utils/numberFormat';
import { generateProfessionalReportPdf, REPORT_META, formatDateBr } from '../utils/reportPdf';
import DateInputPt from './ui/DateInputPt';

const isRecebimentoAprovado = (decisao) =>
  ['aceitar', 'aprovado', 'aprovado_qualidade'].includes(String(decisao || '').toLowerCase());

const isRecebimentoRejeitado = (decisao) =>
  ['rejeitar', 'rejeitado', 'rejeitado_qualidade'].includes(String(decisao || '').toLowerCase());

const ReportsPage = () => {
  const { user, hasPermission, getEmpresaId } = useAuth();
  const [reports, setReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [reportType, setReportType] = useState('geral');
  const [lastUpdate, setLastUpdate] = useState(null);

  const summarizeRecebimentos = (items = []) => {
    const counts = { aprovado: 0, pendente: 0, rejeitado: 0 };
    items.forEach((rec) => {
      if (isRecebimentoAprovado(rec.decisao_final)) counts.aprovado += 1;
      else if (isRecebimentoRejeitado(rec.decisao_final)) counts.rejeitado += 1;
      else counts.pendente += 1;
    });
    return {
      items,
      periodoTotal: items.length,
      counts,
      porUnidade: agregarQuantidadesPorUnidade(items),
      porUnidadeAceita: agregarQuantidadesPorUnidade(items, {
        filterFn: (rec) => isRecebimentoAprovado(rec.decisao_final)
      })
    };
  };

  useEffect(() => {
    if (hasPermission('view_reports') || hasPermission('view_dashboard')) {
      loadReports();
    }
  }, [hasPermission, dateRange, reportType]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 ReportsPage: Carregando relatórios...');

      const empresaId = getEmpresaId();

      // Carregar dados de todas as functions para gerar relatórios
      const statsPayload = empresaId && empresaId !== 'default' ? { empresa_id: empresaId } : {};

      const [
        suppliersResult,
        productsResult,
        documentsResult,
        marketplaceResult,
        receivingsResult,
        usersResult,
        periodReceivingsResult
      ] = await Promise.allSettled([
        httpsCallable(functions, 'getSupplierStats')(statsPayload),
        httpsCallable(functions, 'getProductStats')(statsPayload),
        httpsCallable(functions, 'getDocumentStats')(statsPayload),
        httpsCallable(functions, 'getMarketplaceStats')(statsPayload),
        httpsCallable(functions, 'getReceivingStats')(statsPayload),
        httpsCallable(functions, 'listUsers')({ page: 1, limit: 1000, id_empresa: empresaId }),
        httpsCallable(functions, 'listReceivings')({
          ...statsPayload,
          page: 1,
          limit: 500,
          data_recebimento_inicio: dateRange.start,
          data_recebimento_fim: dateRange.end
        })
      ]);

      const pick = (result, path, fallback = 0) => {
        if (result.status !== 'fulfilled') return fallback;
        let cur = result.value?.data?.data;
        for (const key of path) {
          if (cur == null) return fallback;
          cur = cur[key];
        }
        return cur ?? fallback;
      };

      if (suppliersResult.status === 'rejected') console.warn('getSupplierStats:', suppliersResult.reason);
      if (productsResult.status === 'rejected') console.warn('getProductStats:', productsResult.reason);
      if (documentsResult.status === 'rejected') console.warn('getDocumentStats:', documentsResult.reason);
      if (receivingsResult.status === 'rejected') console.warn('getReceivingStats:', receivingsResult.reason);

      const percentualConformidade = Number(pick(receivingsResult, ['percentual_medio_conformidade'], 0)) || 0;
      // Backend retorna 0–100; card exibe nota 0–10
      const notaQualidade = percentualConformidade / 10;

      const periodItems =
        periodReceivingsResult.status === 'fulfilled'
          ? (periodReceivingsResult.value?.data?.data?.recebimentos || [])
          : [];
      const periodSummary = summarizeRecebimentos(periodItems);

      const recebimentosAprovados =
        periodItems.length > 0
          ? periodSummary.counts.aprovado
          : pick(receivingsResult, ['por_decisao', 'aceitar'], 0);
      const recebimentosRejeitados =
        periodItems.length > 0
          ? periodSummary.counts.rejeitado
          : pick(receivingsResult, ['por_decisao', 'rejeitar'], 0);
      const recebimentosPendentesApi = pick(receivingsResult, ['por_decisao', 'pendente'], 0);
      const totalRecebimentos =
        periodItems.length > 0
          ? periodSummary.periodoTotal
          : pick(receivingsResult, ['total'], 0);
      const recebimentosPendentes =
        periodItems.length > 0
          ? periodSummary.counts.pendente
          : Math.max(
              recebimentosPendentesApi,
              totalRecebimentos - recebimentosAprovados - recebimentosRejeitados
            );

      const qtdPorUnidadeStats = toListaPorUnidade(
        pick(receivingsResult, ['quantidade_por_unidade'], {})
      );
      const qtdAceitaPorUnidadeStats = toListaPorUnidade(
        pick(receivingsResult, ['quantidade_aceita_por_unidade'], {})
      );

      const reportData = {
        total_fornecedores: pick(suppliersResult, ['total'], 0),
        fornecedores_aprovados: pick(suppliersResult, ['por_status', 'aprovado'], 0),
        fornecedores_pendentes: pick(suppliersResult, ['por_status', 'pendente'], 0),
        fornecedores_rejeitados: pick(suppliersResult, ['por_status', 'rejeitado'], 0),

        // getProductStats retorna ativos/inativos (não por_status)
        total_produtos: pick(productsResult, ['total'], 0),
        produtos_ativos: pick(productsResult, ['ativos'], 0),
        produtos_inativos: pick(productsResult, ['inativos'], 0),
        produtos_baixo_estoque: pick(productsResult, ['baixo_estoque'], 0),

        documentos_total: pick(documentsResult, ['total'], 0),
        documentos_aprovados: pick(documentsResult, ['por_status', 'aprovado'], 0),
        documentos_pendentes: pick(documentsResult, ['por_status', 'pendente'], 0),
        documentos_rejeitados: pick(documentsResult, ['por_status', 'rejeitado'], 0),
        documentos_vencendo: pick(documentsResult, ['proximos_vencimento'], 0),
        tempo_medio_aprovacao: pick(documentsResult, ['tempo_medio_aprovacao'], 0),

        total_recebimentos: totalRecebimentos,
        recebimentos_mes: totalRecebimentos,
        // Totais por unidade (nunca somar unidades distintas)
        quantidade_por_unidade:
          periodSummary.porUnidade?.length
            ? periodSummary.porUnidade
            : qtdPorUnidadeStats,
        quantidade_aceita_por_unidade:
          periodSummary.porUnidadeAceita?.length
            ? periodSummary.porUnidadeAceita
            : qtdAceitaPorUnidadeStats,
        nota_qualidade_media: notaQualidade,
        percentual_conformidade: percentualConformidade,

        propostas_total: pick(marketplaceResult, ['total'], 0),
        propostas_ativas: pick(marketplaceResult, ['por_status', 'ativa'], 0),
        propostas_aceitas: pick(marketplaceResult, ['por_status', 'aceita'], 0),
        propostas_rejeitadas: pick(marketplaceResult, ['por_status', 'rejeitada'], 0),

        total_usuarios: usersResult.status === 'fulfilled'
          ? (usersResult.value.data?.data?.length || usersResult.value.data?.usuarios?.length || 0)
          : 0,

        distribuicao_fornecedores: {
          aprovado: pick(suppliersResult, ['por_status', 'aprovado'], 0),
          pendente: pick(suppliersResult, ['por_status', 'pendente'], 0),
          rejeitado: pick(suppliersResult, ['por_status', 'rejeitado'], 0)
        },

        distribuicao_recebimentos: {
          aprovado: recebimentosAprovados,
          pendente: recebimentosPendentes,
          rejeitado: recebimentosRejeitados
        },

        distribuicao_produtos: {
          ativo: pick(productsResult, ['ativos'], 0),
          inativo: pick(productsResult, ['inativos'], 0),
          baixo_estoque: pick(productsResult, ['baixo_estoque'], 0)
        }
      };

      setReports(reportData);
      setLastUpdate(new Date());

      console.log('✅ ReportsPage: Relatórios carregados com sucesso', reportData);

    } catch (error) {
      console.error('❌ ReportsPage: Erro ao carregar relatórios:', error);
      setError('Erro ao carregar relatórios. Tente novamente.');
      
      // Fallback para dados vazios em caso de erro
      setReports({
        total_fornecedores: 0,
        fornecedores_aprovados: 0,
        total_produtos: 0,
        produtos_ativos: 0,
        total_recebimentos: 0,
        quantidade_por_unidade: [],
        quantidade_aceita_por_unidade: [],
        nota_qualidade_media: 0,
        distribuicao_fornecedores: { aprovado: 0, pendente: 0, rejeitado: 0 },
        distribuicao_recebimentos: { aprovado: 0, pendente: 0, rejeitado: 0 },
        distribuicao_produtos: { ativo: 0, inativo: 0, baixo_estoque: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodAnalytics = async () => {
    const empresaId = getEmpresaId();
    const empresaPayload =
      empresaId && empresaId !== 'default' ? { empresa_id: empresaId } : {};

    const empty = {
      items: [],
      periodoTotal: 0,
      counts: {},
      porUnidade: [],
      porUnidadeAceita: []
    };

    try {
      if (reportType === 'recebimentos' || reportType === 'geral') {
        const result = await httpsCallable(functions, 'listReceivings')({
          ...empresaPayload,
          page: 1,
          limit: 500,
          data_recebimento_inicio: dateRange.start,
          data_recebimento_fim: dateRange.end
        });
        const items = result.data?.data?.recebimentos || [];
        return summarizeRecebimentos(items);
      }

      if (reportType === 'documentos') {
        const result = await httpsCallable(functions, 'listDocuments')({
          id_empresa: empresaId,
          page: 1,
          limit: 100,
          data_inicio: dateRange.start,
          data_fim: dateRange.end
        });
        const items = result.data?.data?.items || result.data?.data?.documentos || [];
        const counts = { aprovado: 0, pendente: 0, rejeitado: 0 };
        items.forEach((doc) => {
          const st = String(doc.status || '').toLowerCase();
          if (st === 'aprovado') counts.aprovado += 1;
          else if (st === 'rejeitado' || st === 'recusado') counts.rejeitado += 1;
          else counts.pendente += 1;
        });
        return { items, periodoTotal: items.length, counts, quantidadeTotal: 0 };
      }

      if (reportType === 'fornecedores') {
        const result = await httpsCallable(functions, 'listSuppliers')({
          ...empresaPayload,
          id_empresa: empresaId,
          page: 1,
          limit: 100
        });
        const items =
          result.data?.data?.fornecedores ||
          result.data?.data?.items ||
          result.data?.data ||
          [];
        const list = Array.isArray(items) ? items.slice(0, 100) : [];
        return { items: list, periodoTotal: list.length, counts: {}, quantidadeTotal: 0 };
      }

      if (reportType === 'produtos') {
        const result = await httpsCallable(functions, 'listProducts')({
          ...empresaPayload,
          companyId: empresaId,
          page: 1,
          limit: 100,
          ativo: undefined
        });
        const items =
          result.data?.data?.produtos ||
          result.data?.data?.items ||
          result.data?.data ||
          [];
        const list = Array.isArray(items) ? items.slice(0, 100) : [];
        return { items: list, periodoTotal: list.length, counts: {}, quantidadeTotal: 0 };
      }

      if (reportType === 'marketplace') {
        // Stats já cobrem KPIs; lista detalhada pode não existir
        return empty;
      }

      return empty;
    } catch (err) {
      console.warn('Analytics do período indisponível:', err);
      return empty;
    }
  };

  const getReportSections = (analytics = {}) => {
    const now = new Date();
    const empresa = user?.empresa || user?.nome_empresa || 'Empresa';
    const meta = REPORT_META[reportType] || REPORT_META.geral;
    const periodo = `${formatDateBr(dateRange.start)} a ${formatDateBr(dateRange.end)}`;
    const r = reports;

    const sectionsByType = {
      geral: [
        {
          heading: 'Resumo Executivo',
          lines: [
            `Fornecedores: ${r.total_fornecedores || 0}`,
            `Produtos: ${r.total_produtos || 0}`,
            `Recebimentos no período: ${analytics.periodoTotal ?? r.total_recebimentos ?? 0}`,
            `Quantidades no período: ${formatTotaisPorUnidade(analytics.porUnidade ?? r.quantidade_por_unidade)}`,
            `Qualidade média: ${(r.nota_qualidade_media || 0).toFixed(1)}/10 (${(r.percentual_conformidade || 0).toFixed(1)}% conformidade)`
          ]
        },
        {
          heading: 'Distribuição dos Recebimentos (período)',
          lines: [
            `Aprovados: ${analytics.counts?.aprovado ?? r.distribuicao_recebimentos?.aprovado ?? 0}`,
            `Pendentes: ${analytics.counts?.pendente ?? r.distribuicao_recebimentos?.pendente ?? 0}`,
            `Rejeitados: ${analytics.counts?.rejeitado ?? r.distribuicao_recebimentos?.rejeitado ?? 0}`
          ]
        }
      ],
      fornecedores: [
        {
          heading: 'Indicadores de Fornecedores',
          lines: [
            `Total: ${r.total_fornecedores || 0}`,
            `Aprovados: ${r.distribuicao_fornecedores?.aprovado || 0}`,
            `Pendentes: ${r.distribuicao_fornecedores?.pendente || 0}`,
            `Rejeitados: ${r.distribuicao_fornecedores?.rejeitado || 0}`
          ]
        }
      ],
      produtos: [
        {
          heading: 'Indicadores de Produtos',
          lines: [
            `Total: ${r.total_produtos || 0}`,
            `Ativos: ${r.distribuicao_produtos?.ativo || 0}`,
            `Inativos: ${r.distribuicao_produtos?.inativo || 0}`,
            `Baixo estoque: ${r.distribuicao_produtos?.baixo_estoque || 0}`
          ]
        }
      ],
      documentos: [
        {
          heading: 'Indicadores de Documentos (período)',
          lines: [
            `Itens no período: ${analytics.periodoTotal || 0}`,
            `Aprovados: ${analytics.counts?.aprovado || 0}`,
            `Pendentes: ${analytics.counts?.pendente || 0}`,
            `Rejeitados: ${analytics.counts?.rejeitado || 0}`,
            `Total cadastrado: ${r.documentos_total || 0}`
          ]
        }
      ],
      recebimentos: [
        {
          heading: 'Indicadores de Recebimentos (período)',
          lines: [
            `Total no período: ${analytics.periodoTotal || 0}`,
            `Aprovados: ${analytics.counts?.aprovado || 0}`,
            `Pendentes: ${analytics.counts?.pendente || 0}`,
            `Rejeitados: ${analytics.counts?.rejeitado || 0}`,
            `Quantidades recebidas: ${formatTotaisPorUnidade(analytics.porUnidade)}`,
            `Quantidades aceitas: ${formatTotaisPorUnidade(analytics.porUnidadeAceita)}`,
            `Qualidade média: ${(r.nota_qualidade_media || 0).toFixed(1)}/10`
          ]
        }
      ],
      marketplace: [
        {
          heading: 'Indicadores de Marketplace',
          lines: [
            `Propostas: ${r.propostas_total || 0}`,
            `Ativas: ${r.propostas_ativas || 0}`,
            `Aceitas: ${r.propostas_aceitas || 0}`,
            `Rejeitadas: ${r.propostas_rejeitadas || 0}`
          ]
        }
      ]
    };

    return {
      title: meta.title,
      empresa,
      periodo,
      geradoEm: now.toLocaleString('pt-BR'),
      sections: sectionsByType[reportType] || sectionsByType.geral
    };
  };

  const downloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportReportCsv = (report, analytics = {}) => {
    const lines = [
      `${report.title} - ${report.empresa}`,
      `Periodo: ${report.periodo}`,
      `Gerado em: ${report.geradoEm}`,
      ''
    ];

    report.sections.forEach((section) => {
      lines.push(section.heading);
      section.lines.forEach((line) => lines.push(line));
      lines.push('');
    });

    if (analytics.items?.length) {
      lines.push('ITENS DO PERIODO');
      if (reportType === 'recebimentos' || reportType === 'geral') {
        lines.push('Data;Produto;Fornecedor;Nota Fiscal;Lote;Quantidade;Unidade;Decisao');
        analytics.items.forEach((rec) => {
          const criado = rec.criado_em;
          let dataStr = rec.data_recebimento || '';
          if (!dataStr && criado) {
            try {
              const sec = criado._seconds ?? criado.seconds;
              const d = criado instanceof Date
                ? criado
                : (sec != null ? new Date(sec * 1000) : new Date(criado));
              if (!Number.isNaN(d.getTime())) dataStr = d.toLocaleDateString('pt-BR');
            } catch {
              dataStr = '';
            }
          }
          const lote = Array.isArray(rec.lote_fornecedor)
            ? rec.lote_fornecedor.join(', ')
            : (rec.lote_fornecedor || rec.lote || '');
          lines.push([
            dataStr,
            rec.produto_nome || '',
            rec.fornecedor || '',
            rec.numero_nota_fiscal || '',
            lote,
            formatQuantidade(rec.quantidade_recebida ?? 0),
            rec.unidade_medida || rec.unidade || '',
            rec.decisao_final || 'pendente'
          ].join(';'));
        });
        if (analytics.porUnidade?.length) {
          lines.push('');
          lines.push('TOTAIS POR UNIDADE');
          lines.push('Unidade;Recebida;Aceita');
          const aceitas = Object.fromEntries(
            (analytics.porUnidadeAceita || []).map((row) => [row.unidade, row.quantidade])
          );
          analytics.porUnidade.forEach((row) => {
            lines.push([
              row.unidade,
              formatQuantidade(row.quantidade),
              formatQuantidade(aceitas[row.unidade] || 0)
            ].join(';'));
          });
        }
      } else if (reportType === 'documentos') {
        lines.push('Documento;Tipo;Status;Upload');
        analytics.items.forEach((d) => {
          lines.push([d.nome || '', d.tipo || '', d.status || '', d.data_upload || ''].join(';'));
        });
      } else if (reportType === 'fornecedores') {
        lines.push('Empresa;Status;Categoria');
        analytics.items.forEach((s) => {
          lines.push([s.nome_empresa || s.razao_social || '', s.status || '', s.categoria || ''].join(';'));
        });
      } else if (reportType === 'produtos') {
        lines.push('Produto;Codigo;Categoria;Ativo');
        analytics.items.forEach((p) => {
          lines.push([p.nome || '', p.codigo || '', p.categoria || '', p.ativo === false ? 'Nao' : 'Sim'].join(';'));
        });
      }
    }

    const blob = new Blob(['\uFEFF' + lines.join('\n')], {
      type: 'text/csv;charset=utf-8;'
    });
    downloadBlob(
      blob,
      `relatorio_${reportType}_${dateRange.start}_${dateRange.end}.csv`
    );
  };

  const handleExportReport = async (format) => {
    try {
      setExporting(true);
      setError('');
      console.log(`📊 ReportsPage: Exportando relatório ${reportType} em ${format}`);

      const analytics = await fetchPeriodAnalytics();
      const report = getReportSections(analytics);

      if (format === 'pdf') {
        await generateProfessionalReportPdf({
          reportType,
          empresa: report.empresa,
          dateRange,
          reports,
          analytics,
          geradoPor: user?.email || user?.nome || ''
        });
      } else {
        exportReportCsv(report, analytics);
      }

      console.log(`✅ ReportsPage: Relatório ${format.toUpperCase()} exportado com sucesso`);
    } catch (error) {
      console.error('❌ ReportsPage: Erro ao exportar relatório:', error);
      setError('Erro ao exportar relatório');
    } finally {
      setExporting(false);
    }
  };

  const handleRefresh = () => {
    loadReports();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (!hasPermission('view_reports') && !hasPermission('view_dashboard')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Restrito</h3>
          <p className="text-gray-500">Você não tem permissão para visualizar relatórios.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-gray-600">
            Análises e métricas da empresa
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {lastUpdate && (
            <span className="text-sm text-gray-500">
              Última atualização: {formatDate(lastUpdate)}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Relatório
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="geral">Relatório Geral</option>
              <option value="fornecedores">Fornecedores</option>
              <option value="produtos">Produtos</option>
              <option value="documentos">Documentos</option>
              <option value="recebimentos">Recebimentos</option>
              <option value="marketplace">Marketplace</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Inicial
            </label>
            <DateInputPt
              value={dateRange.start}
              onChange={(ymd) => setDateRange(prev => ({ ...prev, start: ymd }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Final
            </label>
            <DateInputPt
              value={dateRange.end}
              onChange={(ymd) => setDateRange(prev => ({ ...prev, end: ymd }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={() => handleExportReport('csv')}
              disabled={loading || exporting}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              CSV
            </button>
            <button
              onClick={() => handleExportReport('pdf')}
              disabled={loading || exporting}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Carregando relatórios...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Fornecedores */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Fornecedores</p>
                  <p className="text-2xl font-bold text-gray-900">{reports.total_fornecedores || 0}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Truck className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            {/* Produtos */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Produtos</p>
                  <p className="text-2xl font-bold text-gray-900">{reports.total_produtos || 0}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <Package className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            {/* Recebimentos */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Recebimentos</p>
                  <p className="text-2xl font-bold text-gray-900">{reports.total_recebimentos || 0}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <ClipboardCheck className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>

            {/* Qualidade */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Qualidade Média</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(reports.nota_qualidade_media || 0).toFixed(1)}/10
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {(reports.percentual_conformidade || 0).toFixed(1)}% conformidade
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Charts and Detailed Reports */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Distribuição de Fornecedores */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuição de Fornecedores</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Aprovados</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ 
                          width: `${reports.total_fornecedores > 0 ? 
                            (reports.distribuicao_fornecedores?.aprovado / reports.total_fornecedores) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{reports.distribuicao_fornecedores?.aprovado || 0}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pendentes</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-yellow-600 h-2 rounded-full" 
                        style={{ 
                          width: `${reports.total_fornecedores > 0 ? 
                            (reports.distribuicao_fornecedores?.pendente / reports.total_fornecedores) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{reports.distribuicao_fornecedores?.pendente || 0}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Rejeitados</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-red-600 h-2 rounded-full" 
                        style={{ 
                          width: `${reports.total_fornecedores > 0 ? 
                            (reports.distribuicao_fornecedores?.rejeitado / reports.total_fornecedores) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{reports.distribuicao_fornecedores?.rejeitado || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Distribuição dos Recebimentos */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Distribuição dos Recebimentos</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Aprovados</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ 
                          width: `${reports.total_recebimentos > 0 ? 
                            (reports.distribuicao_recebimentos?.aprovado / reports.total_recebimentos) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{reports.distribuicao_recebimentos?.aprovado || 0}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pendentes</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-yellow-600 h-2 rounded-full" 
                        style={{ 
                          width: `${reports.total_recebimentos > 0 ? 
                            (reports.distribuicao_recebimentos?.pendente / reports.total_recebimentos) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{reports.distribuicao_recebimentos?.pendente || 0}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Rejeitados</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-red-600 h-2 rounded-full" 
                        style={{ 
                          width: `${reports.total_recebimentos > 0 ? 
                            (reports.distribuicao_recebimentos?.rejeitado / reports.total_recebimentos) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{reports.distribuicao_recebimentos?.rejeitado || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Totais por unidade (não soma unidades distintas) */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Quantidades por Unidade</h3>
              <p className="text-xs text-gray-500">
                Totais separados por unidade de medida no período
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <div className="flex items-center mb-3">
                  <Scale className="h-5 w-5 text-green-600 mr-2" />
                  <p className="text-sm font-medium text-gray-700">Recebidas</p>
                </div>
                {(reports.quantidade_por_unidade || []).length ? (
                  <ul className="space-y-2">
                    {reports.quantidade_por_unidade.map((row) => (
                      <li key={`rec-${row.unidade}`} className="flex justify-between text-sm">
                        <span className="text-gray-600">{row.unidade}</span>
                        <span className="font-semibold text-gray-900">
                          {formatQuantidadeComUnidade(row.quantidade, row.unidade)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400">Sem quantidades no período</p>
                )}
              </div>
              <div>
                <div className="flex items-center mb-3">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
                  <p className="text-sm font-medium text-gray-700">Aceitas</p>
                </div>
                {(reports.quantidade_aceita_por_unidade || []).length ? (
                  <ul className="space-y-2">
                    {reports.quantidade_aceita_por_unidade.map((row) => (
                      <li key={`ace-${row.unidade}`} className="flex justify-between text-sm">
                        <span className="text-gray-600">{row.unidade}</span>
                        <span className="font-semibold text-gray-900">
                          {formatQuantidadeComUnidade(row.quantidade, row.unidade)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400">Sem quantidades aceitas no período</p>
                )}
              </div>
              <div className="text-center lg:text-left flex flex-col justify-center">
                <ClipboardCheck className="h-8 w-8 text-purple-600 mx-auto lg:mx-0 mb-2" />
                <p className="text-sm text-gray-600">Total de recebimentos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {reports.total_recebimentos || 0}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsPage;

